import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { useQueryClient } from "@tanstack/react-query";
import { authApi, refreshAccess, tokenStore, unwrap, type AuthSchemas } from "@/api/client";
import { browserTimeZone, setTimeZone } from "@/lib/format";

export type User = AuthSchemas["UserOut"];

interface AuthValue {
  user: User | null;
  ready: boolean;
  login: (email: string, password: string, remember: boolean) => Promise<User>;
  logout: () => Promise<void>;
  setUser: (u: User | null) => void;
  reload: () => Promise<void>;
}

const AuthContext = createContext<AuthValue | null>(null);
const REMEMBER_KEY = "picked.remember";
const SESSION_KEY = "picked.session";

function wantsSilentLogin(): boolean {
  try {
    return (
      localStorage.getItem(REMEMBER_KEY) === "1" || sessionStorage.getItem(SESSION_KEY) === "1"
    );
  } catch {
    return false;
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [ready, setReady] = useState(false);
  const qc = useQueryClient();

  const reload = useCallback(async () => {
    const me = unwrap(await authApi.GET("/api/v1/auth/me"));
    setUser(me);
  }, []);

  // Часовой пояс: ручной из профиля, иначе пояс браузера. При автоматике сообщаем
  // серверу пояс устройства, чтобы письма и Telegram тоже шли в нём.
  useEffect(() => {
    if (!user) {
      setTimeZone(null);
      return;
    }
    setTimeZone(user.timezone_auto ? browserTimeZone() : user.timezone);
    if (user.timezone_auto && user.timezone !== browserTimeZone()) {
      authApi.PATCH("/api/v1/auth/me", { body: { timezone: browserTimeZone() } }).catch(() => {});
    }
  }, [user]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (wantsSilentLogin() && (await refreshAccess())) {
        try {
          const me = unwrap(await authApi.GET("/api/v1/auth/me"));
          if (!cancelled) setUser(me);
        } catch {
          /* сессия не восстановилась */
        }
      }
      if (!cancelled) setReady(true);
    })();
    tokenStore.onSessionLost(() => {
      tokenStore.set(null);
      setUser(null);
      qc.clear();
    });
    return () => {
      cancelled = true;
    };
  }, [qc]);

  const login = useCallback(async (email: string, password: string, remember: boolean) => {
    const data = unwrap(
      await authApi.POST("/api/v1/auth/login", { body: { email, password, client: "web" } }),
    );
    tokenStore.set(data.access_token);
    try {
      if (remember) localStorage.setItem(REMEMBER_KEY, "1");
      else localStorage.removeItem(REMEMBER_KEY);
      sessionStorage.setItem(SESSION_KEY, "1");
    } catch {
      /* приватный режим */
    }
    setUser(data.user);
    return data.user;
  }, []);

  const logout = useCallback(async () => {
    try {
      await authApi.POST("/api/v1/auth/logout", { body: {} });
    } finally {
      tokenStore.set(null);
      try {
        localStorage.removeItem(REMEMBER_KEY);
        sessionStorage.removeItem(SESSION_KEY);
      } catch {
        /* ignore */
      }
      setUser(null);
      qc.clear();
    }
  }, [qc]);

  const value = useMemo(
    () => ({ user, ready, login, logout, setUser, reload }),
    [user, ready, login, logout, reload],
  );
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth вне AuthProvider");
  return ctx;
}
